import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui';
import { Button } from '@/components/ui';
import { Input } from '@/components/ui';
import { Label } from '@/components/ui';
import { Switch } from '@/components/ui';
import type { LLMConfig, CreateLLMConfigRequest, UpdateLLMConfigRequest } from '../../types/llm-config';

const SELECT_CLASS =
  'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2';

type LLMConfigDialogProps =
  | {
      mode: 'create';
      open: boolean;
      onOpenChange: (open: boolean) => void;
      onSave: (data: CreateLLMConfigRequest) => void;
      isPending?: boolean;
      defaultValues?: Partial<CreateLLMConfigRequest>;
      llm?: never;
    }
  | {
      mode: 'edit';
      open: boolean;
      onOpenChange: (open: boolean) => void;
      onSave: (data: UpdateLLMConfigRequest) => void;
      isPending?: boolean;
      llm: LLMConfig;
      defaultValues?: never;
    };

type FormData = CreateLLMConfigRequest;

function buildInitialFormData(
  mode: 'create' | 'edit',
  llm?: LLMConfig,
  defaultValues?: Partial<CreateLLMConfigRequest>,
): FormData {
  if (mode === 'edit' && llm) {
    return {
      modelName: llm.modelName,
      modelId: llm.modelId,
      provider: llm.provider,
      displayName: llm.displayName,
      isActive: llm.isActive,
      cachingSupported: llm.cachingSupported,
      reasoningSupported: llm.reasoningSupported,
      visionSupported: llm.visionSupported,
      nativeStructuredOutput: llm.nativeStructuredOutput,
      inputTokenCost: llm.inputTokenCost,
      outputTokenCost: llm.outputTokenCost,
      cacheWriteTokenCost: llm.cacheWriteTokenCost,
      cacheReadTokenCost: llm.cacheReadTokenCost,
      unsupportedParameters: llm.unsupportedParameters,
      reasoningEffort: llm.reasoningEffort,
      thinkingMode: llm.reasoningSupported ? (llm.thinkingMode || 'enabled') : null,
      maxTokens: llm.maxTokens,
      maxThinkingTokens: llm.maxThinkingTokens,
      maxInputTokens: llm.maxInputTokens,
    };
  }
  return {
    modelName: '',
    modelId: '',
    provider: 'bedrock',
    displayName: '',
    isActive: true,
    cachingSupported: false,
    reasoningSupported: false,
    visionSupported: false,
    nativeStructuredOutput: false,
    inputTokenCost: 0,
    outputTokenCost: 0,
    cacheWriteTokenCost: 0,
    cacheReadTokenCost: 0,
    unsupportedParameters: [],
    reasoningEffort: null,
    thinkingMode: null,
    maxTokens: null,
    maxThinkingTokens: null,
    maxInputTokens: null,
    ...defaultValues,
  };
}

function computeChangedFields(formData: FormData, llm: LLMConfig): UpdateLLMConfigRequest {
  const changed: UpdateLLMConfigRequest = {};
  if (formData.modelId !== llm.modelId) changed.modelId = formData.modelId;
  if (formData.provider !== llm.provider) changed.provider = formData.provider;
  if (formData.displayName !== llm.displayName) changed.displayName = formData.displayName;
  if (formData.isActive !== llm.isActive) changed.isActive = formData.isActive;
  if (formData.cachingSupported !== llm.cachingSupported) changed.cachingSupported = formData.cachingSupported;
  if (formData.reasoningSupported !== llm.reasoningSupported) changed.reasoningSupported = formData.reasoningSupported;
  if (formData.visionSupported !== llm.visionSupported) changed.visionSupported = formData.visionSupported;
  if (formData.nativeStructuredOutput !== llm.nativeStructuredOutput) changed.nativeStructuredOutput = formData.nativeStructuredOutput;
  if (formData.inputTokenCost !== llm.inputTokenCost) changed.inputTokenCost = formData.inputTokenCost;
  if (formData.outputTokenCost !== llm.outputTokenCost) changed.outputTokenCost = formData.outputTokenCost;
  if (formData.cacheWriteTokenCost !== llm.cacheWriteTokenCost) changed.cacheWriteTokenCost = formData.cacheWriteTokenCost;
  if (formData.cacheReadTokenCost !== llm.cacheReadTokenCost) changed.cacheReadTokenCost = formData.cacheReadTokenCost;
  if (JSON.stringify(formData.unsupportedParameters) !== JSON.stringify(llm.unsupportedParameters)) changed.unsupportedParameters = formData.unsupportedParameters;
  if (formData.reasoningEffort !== llm.reasoningEffort) changed.reasoningEffort = formData.reasoningEffort;
  if (formData.thinkingMode !== llm.thinkingMode) changed.thinkingMode = formData.thinkingMode;
  if (formData.maxTokens !== llm.maxTokens) changed.maxTokens = formData.maxTokens;
  if (formData.maxThinkingTokens !== llm.maxThinkingTokens) changed.maxThinkingTokens = formData.maxThinkingTokens;
  if (formData.maxInputTokens !== llm.maxInputTokens) changed.maxInputTokens = formData.maxInputTokens;
  return changed;
}

export const LLMConfigDialog: React.FC<LLMConfigDialogProps> = (props) => {
  const { mode, open, onOpenChange, onSave, isPending = false } = props;
  const llm = mode === 'edit' ? props.llm : undefined;
  const defaultValues = mode === 'create' ? props.defaultValues : undefined;
  const isCreate = mode === 'create';

  const getInitial = () => buildInitialFormData(mode, llm, defaultValues);

  const [formData, setFormData] = useState<FormData>(getInitial);
  const [unsupportedParamsInput, setUnsupportedParamsInput] = useState(
    () => (llm?.unsupportedParameters ?? defaultValues?.unsupportedParameters ?? []).join(', '),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});

  const isBedrock = formData.provider === 'bedrock';

  React.useEffect(() => {
    if (open) {
      const initial = getInitial();
      setFormData(initial);
      setUnsupportedParamsInput(initial.unsupportedParameters?.join(', ') || '');
      setErrors({});
    }
  }, [open, llm, defaultValues]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const newErrors: Record<string, string> = {};
    if (isCreate && !formData.modelName.trim()) newErrors.modelName = 'Model name is required';
    if (!formData.modelId.trim()) newErrors.modelId = 'Model ID is required';
    if (!formData.provider) newErrors.provider = 'Provider is required';
    if (!formData.displayName.trim()) newErrors.displayName = 'Display name is required';
    if (formData.inputTokenCost < 0) newErrors.inputTokenCost = 'Must be non-negative';
    if (formData.outputTokenCost < 0) newErrors.outputTokenCost = 'Must be non-negative';
    if (formData.cacheWriteTokenCost !== undefined && formData.cacheWriteTokenCost < 0) newErrors.cacheWriteTokenCost = 'Must be non-negative';
    if (formData.cacheReadTokenCost !== undefined && formData.cacheReadTokenCost < 0) newErrors.cacheReadTokenCost = 'Must be non-negative';

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    if (mode === 'edit' && llm) {
      (onSave as (data: UpdateLLMConfigRequest) => void)(computeChangedFields(formData, llm));
    } else {
      (onSave as (data: CreateLLMConfigRequest) => void)(formData);
    }
  };

  const handleClose = () => {
    setFormData(getInitial());
    setUnsupportedParamsInput('');
    setErrors({});
    onOpenChange(false);
  };

  const update = (partial: Partial<FormData>) => {
    setFormData((prev) => ({ ...prev, ...partial }));
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>{isCreate ? 'Add LLM Model' : 'Edit LLM Model'}</DialogTitle>
          <DialogDescription>
            {isCreate
              ? 'Configure a new LLM model. The model ID should match the identifier used by the provider.'
              : <>Update the configuration for <span className="font-mono">{llm?.modelName}</span></>}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 overflow-hidden">
          <div className="grid gap-4 py-4 px-1 overflow-y-auto pr-2">

            <SwitchField id="isActive" label="Active" description="Make this model available for use" checked={formData.isActive ?? false} onCheckedChange={(v) => update({ isActive: v })} disabled={isPending} />

            {/* Model Name — create: editable, edit: read-only */}
            <div className="grid gap-2">
              <Label htmlFor="modelName">
                Model Name {isCreate && <span className="text-destructive">*</span>}
              </Label>
              {isCreate ? (
                <Input
                  id="modelName"
                  placeholder="e.g., bedrock_claude_45_sonnet"
                  value={formData.modelName}
                  onChange={(e) => { update({ modelName: e.target.value }); setErrors({ ...errors, modelName: '' }); }}
                  disabled={isPending}
                  className={errors.modelName ? 'border-destructive' : ''}
                />
              ) : (
                <Input id="modelName" value={llm?.modelName ?? ''} disabled className="bg-muted" />
              )}
              {errors.modelName && <p className="text-sm text-destructive">{errors.modelName}</p>}
              {isCreate && (
                <p className="text-xs text-muted-foreground">
                  A unique identifier for this model configuration
                </p>
              )}
              {!isCreate && (
                <p className="text-xs text-muted-foreground">Model name cannot be changed after creation</p>
              )}
            </div>

            {/* Provider */}
            <div className="grid gap-2">
              <Label htmlFor="provider">
                Provider {isCreate && <span className="text-destructive">*</span>}
              </Label>
              <select
                id="provider"
                value={formData.provider}
                onChange={(e) => {
                  const provider = e.target.value as 'bedrock' | 'openai';
                  update({
                    provider,
                    thinkingMode: provider === 'bedrock' ? 'enabled' : null,
                  });
                  setErrors({ ...errors, provider: '' });
                }}
                disabled={isPending}
                className={`${SELECT_CLASS} ${errors.provider ? 'border-destructive' : ''}`}
              >
                <option value="bedrock">AWS Bedrock</option>
                <option value="openai">OpenAI</option>
              </select>
              {errors.provider && <p className="text-sm text-destructive">{errors.provider}</p>}
            </div>

            {/* Model ID */}
            <div className="grid gap-2">
              <Label htmlFor="modelId">
                Model ID <span className="text-destructive">*</span>
              </Label>
              <Input
                id="modelId"
                placeholder={isBedrock ? 'e.g., us.anthropic.claude-sonnet-4-20250514-v1:0' : 'e.g., gpt-4o'}
                value={formData.modelId}
                onChange={(e) => { update({ modelId: e.target.value }); setErrors({ ...errors, modelId: '' }); }}
                disabled={isPending}
                className={errors.modelId ? 'border-destructive' : ''}
              />
              {errors.modelId && <p className="text-sm text-destructive">{errors.modelId}</p>}
              <p className="text-xs text-muted-foreground">
                The unique identifier for this model as used by the provider
              </p>
            </div>

            {/* Display Name */}
            <div className="grid gap-2">
              <Label htmlFor="displayName">
                Display Name <span className="text-destructive">*</span>
              </Label>
              <Input
                id="displayName"
                placeholder={isBedrock ? 'e.g., Claude Sonnet 4' : 'e.g., GPT-4o'}
                value={formData.displayName}
                onChange={(e) => { update({ displayName: e.target.value }); setErrors({ ...errors, displayName: '' }); }}
                disabled={isPending}
                className={errors.displayName ? 'border-destructive' : ''}
              />
              {errors.displayName && <p className="text-sm text-destructive">{errors.displayName}</p>}
              <p className="text-xs text-muted-foreground">A human-readable name shown in the UI</p>
            </div>

            {/* Token costs */}
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label htmlFor="inputTokenCost">
                  Input Token Cost (per 1K) <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="inputTokenCost"
                  type="number" step="0.000001" min="0" placeholder="e.g., 0.003"
                  value={formData.inputTokenCost}
                  onChange={(e) => { update({ inputTokenCost: parseFloat(e.target.value) || 0 }); setErrors({ ...errors, inputTokenCost: '' }); }}
                  disabled={isPending}
                  className={errors.inputTokenCost ? 'border-destructive' : ''}
                />
                {errors.inputTokenCost && <p className="text-sm text-destructive">{errors.inputTokenCost}</p>}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="outputTokenCost">
                  Output Token Cost (per 1K) <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="outputTokenCost"
                  type="number" step="0.000001" min="0" placeholder="e.g., 0.015"
                  value={formData.outputTokenCost}
                  onChange={(e) => { update({ outputTokenCost: parseFloat(e.target.value) || 0 }); setErrors({ ...errors, outputTokenCost: '' }); }}
                  disabled={isPending}
                  className={errors.outputTokenCost ? 'border-destructive' : ''}
                />
                {errors.outputTokenCost && <p className="text-sm text-destructive">{errors.outputTokenCost}</p>}
              </div>
            </div>

            {/* Caching toggle + conditional cache costs */}
            <SwitchField id="cachingSupported" label="Caching Supported" description="Enable if this model supports prompt caching" checked={formData.cachingSupported ?? false} onCheckedChange={(v) => update({ cachingSupported: v })} disabled={isPending} />

            {formData.cachingSupported && (
              <div className={`grid ${isBedrock ? 'grid-cols-2' : ''} gap-4`}>
                {isBedrock && (
                  <div className="grid gap-2">
                    <Label htmlFor="cacheWriteTokenCost">Cache Write Cost (per 1K)</Label>
                    <Input
                      id="cacheWriteTokenCost"
                      type="number" step="0.000001" min="0" placeholder="e.g., 0.00375"
                      value={formData.cacheWriteTokenCost}
                      onChange={(e) => { update({ cacheWriteTokenCost: parseFloat(e.target.value) || 0 }); setErrors({ ...errors, cacheWriteTokenCost: '' }); }}
                      disabled={isPending}
                      className={errors.cacheWriteTokenCost ? 'border-destructive' : ''}
                    />
                    {errors.cacheWriteTokenCost && <p className="text-sm text-destructive">{errors.cacheWriteTokenCost}</p>}
                  </div>
                )}
                <div className="grid gap-2">
                  <Label htmlFor="cacheReadTokenCost">Cached Input Cost (per 1K)</Label>
                  <Input
                    id="cacheReadTokenCost"
                    type="number" step="0.000001" min="0" placeholder="e.g., 0.0003"
                    value={formData.cacheReadTokenCost}
                    onChange={(e) => { update({ cacheReadTokenCost: parseFloat(e.target.value) || 0 }); setErrors({ ...errors, cacheReadTokenCost: '' }); }}
                    disabled={isPending}
                    className={errors.cacheReadTokenCost ? 'border-destructive' : ''}
                  />
                  {errors.cacheReadTokenCost && <p className="text-sm text-destructive">{errors.cacheReadTokenCost}</p>}
                </div>
              </div>
            )}

            {/* Capability toggles */}
            <SwitchField id="visionSupported" label="Vision Supported" description="Enable if this model supports image/vision inputs" checked={formData.visionSupported ?? false} onCheckedChange={(v) => update({ visionSupported: v })} disabled={isPending} />
            <SwitchField
              id="nativeStructuredOutput"
              label="Native Structured Output"
              description={isBedrock ? 'Use constrained decoding instead of tool_use for JSON output' : 'Use structured output mode for JSON responses'}
              checked={formData.nativeStructuredOutput ?? false}
              onCheckedChange={(v) => update({ nativeStructuredOutput: v })}
              disabled={isPending}
            />

            {/* Bedrock: Thinking mode + conditional sub-fields */}
            {isBedrock && (
              <div className="grid gap-2">
                <Label htmlFor="thinkingMode">Thinking Mode</Label>
                <select
                  id="thinkingMode"
                  value={formData.reasoningSupported ? (formData.thinkingMode || 'enabled') : 'none'}
                  onChange={(e) => {
                    const val = e.target.value;
                    if (val === 'none') {
                      update({ reasoningSupported: false, thinkingMode: null, reasoningEffort: null, maxThinkingTokens: null });
                    } else if (val === 'enabled') {
                      update({ reasoningSupported: true, thinkingMode: val, reasoningEffort: null });
                    } else {
                      update({ reasoningSupported: true, thinkingMode: val, maxThinkingTokens: null });
                    }
                  }}
                  disabled={isPending}
                  className={SELECT_CLASS}
                >
                  <option value="none">None</option>
                  <option value="enabled">Enabled (budget_tokens)</option>
                  <option value="adaptive">Adaptive (Opus 4.6+)</option>
                </select>
                <p className="text-xs text-muted-foreground">
                  Extended thinking mode. 'Enabled' uses fixed budget_tokens, 'Adaptive' lets the model decide.
                </p>
              </div>
            )}

            {/* Bedrock adaptive: reasoning effort */}
            {isBedrock && formData.reasoningSupported && formData.thinkingMode === 'adaptive' && (
              <div className="grid gap-2">
                <Label htmlFor="reasoningEffort">Reasoning Effort</Label>
                <select
                  id="reasoningEffort"
                  value={formData.reasoningEffort || ''}
                  onChange={(e) => update({ reasoningEffort: e.target.value || null })}
                  disabled={isPending}
                  className={SELECT_CLASS}
                >
                  <option value="">Default (high)</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="max">Max (Opus 4.6)</option>
                </select>
                <p className="text-xs text-muted-foreground">Controls adaptive thinking effort level</p>
              </div>
            )}

            {/* Bedrock enabled: max thinking tokens */}
            {isBedrock && formData.reasoningSupported && formData.thinkingMode === 'enabled' && (
              <div className="grid gap-2">
                <Label htmlFor="maxThinkingTokens">Max Thinking Tokens</Label>
                <Input
                  id="maxThinkingTokens"
                  type="number" min="1024" placeholder="e.g., 10000"
                  value={formData.maxThinkingTokens ?? ''}
                  onChange={(e) => update({ maxThinkingTokens: e.target.value ? parseInt(e.target.value, 10) : null })}
                  disabled={isPending}
                />
                <p className="text-xs text-muted-foreground">Fixed token budget for thinking (minimum 1024)</p>
              </div>
            )}

            {/* OpenAI: reasoning effort (shown directly, no thinking mode wrapper) */}
            {!isBedrock && (
              <div className="grid gap-2">
                <Label htmlFor="reasoningEffort">Reasoning Effort</Label>
                <select
                  id="reasoningEffort"
                  value={formData.reasoningEffort || ''}
                  onChange={(e) => {
                    const val = e.target.value || null;
                    update({ reasoningEffort: val, reasoningSupported: val !== null });
                  }}
                  disabled={isPending}
                  className={SELECT_CLASS}
                >
                  <option value="">Default (model-dependent)</option>
                  <option value="none">None</option>
                  <option value="minimal">Minimal</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="xhigh">Extra High</option>
                </select>
                <p className="text-xs text-muted-foreground">
                  Controls reasoning effort. Default varies by model (medium for o-series, none for GPT-5.2+).
                </p>
              </div>
            )}

            {/* Shared optional fields */}
            <div className="grid gap-2">
              <Label htmlFor="unsupportedParameters">Unsupported Parameters</Label>
              <Input
                id="unsupportedParameters"
                placeholder="e.g., temperature, top_p, max_tokens"
                value={unsupportedParamsInput}
                onChange={(e) => {
                  setUnsupportedParamsInput(e.target.value);
                  const params = e.target.value.split(',').map(p => p.trim()).filter(p => p.length > 0);
                  update({ unsupportedParameters: params });
                }}
                disabled={isPending}
              />
              <p className="text-xs text-muted-foreground">Comma-separated list of parameters not supported by this model</p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="maxInputTokens">Max Input Tokens</Label>
              <Input
                id="maxInputTokens"
                type="number" min="1" placeholder="e.g., 200000"
                value={formData.maxInputTokens ?? ''}
                onChange={(e) => update({ maxInputTokens: e.target.value ? parseInt(e.target.value, 10) : null })}
                disabled={isPending}
              />
              <p className="text-xs text-muted-foreground">Maximum input tokens the model supports</p>
            </div>

            <div className="grid gap-2">
              <Label htmlFor="maxTokens">Max Output Tokens</Label>
              <Input
                id="maxTokens"
                type="number" min="1" placeholder="e.g., 4096"
                value={formData.maxTokens ?? ''}
                onChange={(e) => update({ maxTokens: e.target.value ? parseInt(e.target.value, 10) : null })}
                disabled={isPending}
              />
              <p className="text-xs text-muted-foreground">Maximum tokens to generate in the response</p>
            </div>

            {/* Audit info — edit only */}
            {!isCreate && llm && (
              <div className="grid grid-cols-2 gap-4 pt-2 text-xs text-muted-foreground">
                <div><p className="font-medium">Created by:</p><p>{llm.createdBy}</p></div>
                <div><p className="font-medium">Created at:</p><p>{new Date(llm.createdAt).toLocaleString()}</p></div>
                <div><p className="font-medium">Updated by:</p><p>{llm.updatedBy}</p></div>
                <div><p className="font-medium">Updated at:</p><p>{new Date(llm.updatedAt).toLocaleString()}</p></div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? (isCreate ? 'Creating...' : 'Saving...') : (isCreate ? 'Create Model' : 'Save Changes')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

/** Inline helper to reduce toggle boilerplate. */
function SwitchField({ id, label, description, checked, onCheckedChange, disabled }: {
  id: string; label: string; description: string;
  checked: boolean; onCheckedChange: (v: boolean) => void; disabled?: boolean;
}) {
  return (
    <div className="flex items-center justify-between space-x-2 rounded-lg border p-4">
      <div className="space-y-0.5">
        <Label htmlFor={id} className="text-base">{label}</Label>
        <p className="text-sm text-muted-foreground">{description}</p>
      </div>
      <Switch id={id} checked={checked} onCheckedChange={onCheckedChange} disabled={disabled} />
    </div>
  );
}
